<!-- Generic Text Field -->
<template>
  <div>
    
    <v-row style="align-items: center;">
      <v-col cols="12" md="12" sm="12"> 
        <div class="input-helper">
          <div class="element"> 
            <v-text-field 
              class="textInput"
              v-model="cmpValue"
              :ref="$attrs.ref"
              :type="type"
              :maxlength="$attrs.maxlength"
              :max="$attrs.max"
              :rules="rules"
              v-bind="$attrs"
              v-on="$listeners"
              :label="cmpLabel"
              @input="_input"
              @blur="_input"
              @change="_input"
              @paste="_paste"
              @keypress="_keyPress">

              <template slot="append" v-if="$attrs.disabled">
                <v-icon tabindex="-1" color="stepper">mdi-check-circle</v-icon>
              </template>

              <template slot="append" v-if="!$attrs.disabled">
                <v-btn x-small fab class="cleanBtn" 
                  elevation="0"
                  tabindex="-1"
                  v-if="cmpValue != null && cmpValue != ''" 
                  @click="_clear">
                  <v-icon>mdi-close</v-icon>
                </v-btn>
              </template>

              <template slot="append" v-for="(item, idx) in appendIcons">
                <v-icon tabindex="-1" :key="idx" @click="_iconAction(item.event)">{{item.icon}}</v-icon>
              </template>
            </v-text-field>
          </div>

          <div class="flex-btn"> 
            <v-btn x-small fab class="helperBtn" v-if="!!helperText"
              elevation="0"
              tabindex="-1"
              @click="_showHelperText">
              <v-icon>mdi-help</v-icon>
            </v-btn>
          </div>
        </div>
      </v-col>
    </v-row>

    <HelperDialog
      :helperText="helperText"
      :show="helperTextPopUpVisible"
      @close="_closeHelperText">
    </HelperDialog>

  </div>
</template>

<script>

  import HelperDialog from "./helper-dialog.vue"
  import regExpressions from "@/shared/constants/regexpressions.js";
  import generic from '@/shared/utils/generic.js';

  export default {
    name: 'TextField',
    inheritAttrs: false,
    components: {
      HelperDialog
    },
  props: {
      label: { type: String },
      value: { type: String },
      rules: { type: Array },
      helperText: { type: String },
      appendIcons: { type: Array }, // Ex: [{icon: 'mdi-crosshairs', event: 'confirm'}], -> "crosshairs" iconClick will emit "confirm" event
      type: { 
        type: String, 
        default: 'text', 
        // Only allow types that essentially just render text boxes.
        validator(value) { return ['email', 'number', 'password', 'text'].includes(value) }, 
      },
    },
    emits: ["input", "clear"],
    data: () => ({
      helperTextPopUpVisible: false,
      isRequired: undefined,
    }),
    watch: {
      rules: {
        immediate: true,
        handler(value) {
          // text-field is the only one that can have its rules changed after creation
          // The watch allows to change "isRequired" data attribute when the rules are changed from the outside
          this.isRequired = generic.isRequired(value);
        }
      }
    },
    methods: {
      _clear() { 
        this.cmpValue = '';
        this.$emit("clear"); 
      },

      _iconAction(eventName) {
        if (eventName) this.$emit(eventName, this.cmpValue);
        else return true;
      },

      _input() { 
        // Trim content to prevent white spaces as content
        this.cmpValue = this.value != null && this.value.trim ? this.value.trim() : this.value;
        this.$emit("input", this.cmpValue); 
      },
      
      _paste($event) {
        if (this.type === 'number') {
          const pastedValue = $event.clipboardData.getData('text');
          if (pastedValue && (!pastedValue.isNumeric() || this.numberOutsideBoundaries(pastedValue))) $event.preventDefault();
        }
      },

      _keyPress($event) {
        // "EMail" will alow all chars as the validation will prevent to proceed if an invalid email is used
        if (this.type === 'email') return true;
        if (this.type === 'number') {
          let keyCode = $event.keyCode ? $event.keyCode : $event.which;
          if (keyCode < 48 || keyCode > 57 || this.numberOutsideBoundaries($event.key) || this.targetLength($event)) $event.preventDefault();
        }
        else if (!regExpressions.GenericTexts.test($event.key)) $event.preventDefault();
      },

      _showHelperText () {
        this.helperTextPopUpVisible = !!this.helperText;
      },

      _closeHelperText() {
        this.helperTextPopUpVisible = false;
      },

      targetLength($event) {
        const value = $event.target.value + $event.key;
        if (!value || $event.target.maxLength === -1) return false; // $event.target.maxLength === -1 // Not Set
        return value.length > $event.target.maxLength;
      },
      
      numberOutsideBoundaries(addedValue) {
        const numericValue = parseFloat(generic.cleanNumber(this.cmpValue ? this.cmpValue : 0) + generic.cleanNumber(addedValue));
        let ret = false;
        if (this.$attrs.max) ret = ret || (numericValue > this.$attrs.max);
        if (this.$attrs.min || this.$attrs.min == 0) ret = ret || (numericValue < this.$attrs.min);
        return ret;
      }
    },

    computed: {
      cmpValue: {
        get: function () { return this.value; },
        set: function (newValue) { this.$emit("input", newValue); },
      },
      cmpLabel: {
        get() { return this.label + (this.isRequired ? ' *' : '') },
      }
    },
  };
</script>

<style scoped>
  .textInput>>>input[type="number"] {
    -moz-appearance: textfield;
  }

  .textInput>>>input::-webkit-outer-spin-button,
  .textInput>>>input::-webkit-inner-spin-button {
    appearance: none;
    -webkit-appearance: none;
    -moz-appearance: none;
  }
</style>